import React from 'react';
import { Checkbox, Button, Divider, theme } from 'components';
import styled from 'styled-components';
import { FilterDropdownProps } from 'antd/lib/table/interface';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { featureEnabled } from 'utils';
import { LegacyInvoiceFilterStatus, InvoiceFilterStatus } from './types';

const StatusFilterDropdown: React.FC<FilterDropdownProps> = ({
  confirm,
  setSelectedKeys,
}) => {
  let statuses;
  if (featureEnabled('keyholderBillingFeature')) {
    statuses = InvoiceFilterStatus;
  } else {
    statuses = LegacyInvoiceFilterStatus;
  }
  const [selectedStatus, setSelectedStatus] = React.useState<
    LegacyInvoiceFilterStatus | InvoiceFilterStatus
  >();
  const filterDropdownRef = React.useRef<HTMLDivElement>(null);

  const onSubmit = React.useCallback(() => {
    setSelectedKeys([selectedStatus].filter(Boolean) as React.Key[]);
    confirm();
  }, [selectedStatus, confirm, setSelectedKeys]);

  const onCheckboxChange = (e: CheckboxChangeEvent) => {
    setSelectedStatus(e.target.checked ? e.target.value : undefined);
  };

  const onReset = () => {
    setSelectedStatus(undefined);
  };

  React.useEffect(() => {
    /*
     When the user clicks outside of the dialog, this handler just acts 
     like the OK button was pushed instead of cancel action.
    */
    const handleClickOutside = (e: MouseEvent) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(e.target as Node)
      ) {
        onSubmit();
      }
    };

    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [onSubmit]);

  return (
    <StyledFilter ref={filterDropdownRef}>
      <StyledContainer>
        <StyledCheckboxContainer>
          {Object.keys(statuses).map((status, idx) => {
            const value = Object.values(statuses)[idx];
            return (
              <div>
                <Checkbox
                  value={value}
                  checked={selectedStatus === value}
                  key={status}
                  onChange={onCheckboxChange}
                >
                  {status}
                </Checkbox>
              </div>
            );
          })}
        </StyledCheckboxContainer>
        <StyledDivider />
        <StyledButtonContainer>
          <StyledResetButton
            type="text"
            disabled={!selectedStatus}
            onClick={onReset}
          >
            Reset
          </StyledResetButton>
          <Button type="primary" onClick={onSubmit}>
            Ok
          </Button>
        </StyledButtonContainer>
      </StyledContainer>
    </StyledFilter>
  );
};

export default StatusFilterDropdown;

const StyledFilter = styled.div`
  min-width: 120px;
  max-width: 200px;
  width: fit-content;
`;

const StyledDivider = styled(Divider)`
  margin: 0;
`;

const StyledCheckboxContainer = styled.div`
  padding: 10px;
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledResetButton = styled(Button)`
  color: ${theme.blue5};
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 5px;
  padding: 10px;
`;
